// WHAT: Modal component for creating and editing roadmap items
// WHY:  Provides a comprehensive form interface for managing roadmap item properties
// DOMAIN: Admin → Roadmap → Overview
// DEPENDENCIES: Roadmap_Item_Schema for validation, useRoadmapCreate/Update hooks, ModuleSelect component
// RISKS:
//   - WARN: Uses crypto.randomUUID() for new item IDs - ensure browser support
//   - NOTE: Automatic status/progress synchronization may be unexpected for users
//   - NOTE: Form validation uses Zod schema - errors may not be user-friendly

import { useEffect, useState } from "react";

import { Modal, Button, Slider } from "@/components/ui";
import { ModuleSelect } from "@/domains/admin/roadmap/components";
import { useRoadmapContext } from "@/domains/admin/roadmap/context";
import { useRoadmapCreate, useRoadmapUpdate } from "@/domains/admin/roadmap/hooks";
import { Roadmap_Item_Schema } from "@/domains/admin/roadmap/shared";
import {
  type Roadmap_Item,
  type Roadmap_Item_Type,
  type Roadmap_Item_Status,
} from "@/domains/admin/roadmap/types/roadmap-types";

/**
 * Props for EditModal component
 */
interface Properties {
  /** Existing item to edit (undefined for new items) */
  item?: Roadmap_Item;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * WHAT:
 *   Normalizes enum values to lowercase for consistency.
 *
 * WHY:
 *   Ensures enum values are stored in lowercase format regardless of input case.
 *
 * BEHAVIOR:
 *   - Converts string values to lowercase
 *   - Returns undefined for non-string values
 *
 * @param v - The enum value to normalize
 *
 * @returns {T | undefined} Normalized enum value or undefined
 *
 */
function normalizeEnum<T extends string>(v?: T): T | undefined {
  return typeof v === "string" ? (v.toLowerCase() as T) : undefined;
}

/**
 * WHAT:
 *   Modal component for creating and editing roadmap items with comprehensive
 *   form fields and automatic status/progress synchronization.
 *
 * WHY:
 *   Provides a unified interface for item creation and editing, ensuring data
 *   consistency through schema validation and automatic field synchronization.
 *
 * BEHAVIOR:
 *   - Supports both create and edit modes
 *   - Validates form data using Zod schema
 *   - Automatically syncs status and progress (done ↔ 100%)
 *   - Normalizes enum values to lowercase
 *   - Updates context state after successful save
 *
 * RISKS:
 *   - WARN: crypto.randomUUID() may not be available in all browsers - add fallback
 *   - NOTE: Automatic status/progress sync may override user intentions
 *   - NOTE: Form errors from Zod may not be user-friendly - consider custom messages
 *   - NOTE: Tags are comma-separated - no validation for empty tags
 *
 * @param root0
 *
 * @param root0.item
 *
 * @param root0.onClose
 *
 * @returns {JSX.Element} Die gerenderte EditModal-Komponente
 *
 */
export const EditModal = ({ item, onClose }: Properties) => {
  // NOTE: Determine if editing existing item or creating new one
  const isEdit = !!item;
  // NOTE: Get context for updating items state
  const { items, setItems } = useRoadmapContext();
  // NOTE: Hooks for create and update operations
  const createItem = useRoadmapCreate();
  const updateItem = useRoadmapUpdate();

  // NOTE: Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  // NOTE: Submission state to prevent double-submission
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState<Roadmap_Item>(() => ({
    created_at: item?.created_at ?? new Date().toISOString(),
    description: item?.description ?? "",
    effort_estimate: normalizeEnum(item?.effort_estimate) ?? "medium",
    id: item?.id ?? crypto.randomUUID(),
    impact_score: normalizeEnum(item?.impact_score) ?? "medium",
    linked_to: item?.linked_to ?? [],
    module: item?.module ?? "",
    priority: normalizeEnum(item?.priority) ?? "medium",
    status: normalizeEnum(item?.status) ?? "open",
    tags: item?.tags ?? [],
    title: item?.title ?? "",
    type: normalizeEnum(item?.type) ?? "feature",
    updated_at: new Date().toISOString(),
  }));

  useEffect(() => {
    if (!item) {
      return;
    }
    setForm({
      created_at: item.created_at,
      description: item.description,
      effort_estimate: normalizeEnum(item.effort_estimate) ?? "medium",
      id: item.id,
      impact_score: normalizeEnum(item.impact_score) ?? "medium",
      linked_to: item.linked_to ?? [],
      module: item.module,
      priority: normalizeEnum(item.priority) ?? "medium",
      progress: item.progress ?? 0,
      status: normalizeEnum(item.status) ?? "open",
      tags: item.tags ?? [],
      title: item.title,
      type: normalizeEnum(item.type) ?? "feature",
      updated_at: new Date().toISOString(),
    });
  }, [item]);

  /**
   * Normalized value type
   */
  type NormalizedValue = number | string | string[] | undefined;

  /**
   * WHAT:
   *   Normalizes field values based on field type (lowercase for enum fields).
   *
   * WHY:
   *   Ensures enum fields are stored in lowercase for consistency.
   *
   * BEHAVIOR:
   *   - Converts enum fields (type, status, priority, impact_score, effort_estimate) to lowercase
   *   - Leaves other fields unchanged
   *
   * @param k - The field key
   *
   * @param v - The field value
   *
   * @returns Normalized value
   *
   */
  const normalize = (k: string, v: NormalizedValue): NormalizedValue => {
    const lowerFields = ["type", "status", "priority", "impact_score", "effort_estimate"];
    const result = lowerFields.includes(k) && typeof v === "string" ? v.toLowerCase() : v;
    return result;
  };

  /**
   * WHAT:
   *   Handles form field changes with automatic status/progress synchronization.
   *
   * WHY:
   *   Ensures status and progress fields stay in sync (done ↔ 100%).
   *
   * BEHAVIOR:
   *   - Normalizes field values
   *   - Sets progress to 100% when status changes to 'done'
   *   - Sets status to 'done' when progress changes to 100%
   *   - Updates form state with normalized value
   *
   * RISKS:
   *   - NOTE: Automatic sync may override user intentions
   *   - NOTE: Users may not expect status to change when adjusting progress
   *
   * @param key
   *
   * @param value
   *
   */
  const handleChange = (key: keyof Roadmap_Item, value: number | string | string[] | undefined) => {
    const normalized = normalize(key as string, value);

    // NOTE: Automatisch Progress auf 100% setzen wenn Status auf "done" geändert wird
    if (key === "status" && normalized === "done") {
      setForm((previous) => ({
        ...previous,
        [key]: normalized,
        progress: 100, // NOTE: Automatisch 100% wenn Done
      }));
    }
    // NOTE: Automatisch Status auf "done" setzen wenn Progress auf 100% gesetzt wird
    else if (key === "progress" && normalized === 100) {
      setForm((previous) => ({
        ...previous,
        [key]: normalized,
        status: "done" as const, // NOTE: Automatisch Done wenn 100%
      }));
    } else {
      setForm((previous) => ({
        ...previous,
        [key]: normalized,
      }));
    }
  };

  /**
   * Extracts field errors from Zod validation issues.
   *
   * @param issues - Array of Zod validation issues
   *
   * @returns Record of field errors
   *
   */
  const extractFieldErrors = (
    issues: { message: string; path: (number | string)[] }[],
  ): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};
    for (const issue of issues) {
      const pathKey = issue.path[0];
      if (pathKey && (typeof pathKey === "string" || typeof pathKey === "number")) {
        fieldErrors[String(pathKey)] = issue.message;
      }
    }
    return fieldErrors;
  };

  const handleSubmit = async () => {
    const parseResult = Roadmap_Item_Schema.safeParse(form);

    if (!parseResult.success) {
      const zodError = parseResult.error;
      const issues = zodError.issues;
      const fieldErrors = extractFieldErrors(
        issues.map((issue) => ({
          message: issue.message,
          path: issue.path.filter(
            (p): p is number | string => typeof p === "string" || typeof p === "number",
          ),
        })),
      );
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    const action = isEdit ? updateItem : createItem;
    const { error, success } = await action(form);

    if (success) {
      const updatedItems = isEdit
        ? items.map((index) => (index.id === form.id ? form : index))
        : [...items, form];

      setItems(updatedItems);
      onClose();
    } else {
      setErrors({ form: error ?? "Unbekannter Fehler" });
    }

    setSubmitting(false);
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" title={isEdit ? "Edit Item" : "New Roadmap Item"}>
      <div className="space-y-6">
        {errors.form ? (
          <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-400 text-sm">{errors.form}</p>
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">Title</label>
          <input
            className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
            onChange={(e) => {
              handleChange("title", e.target.value);
            }}
            placeholder="Title"
            type="text"
            value={form.title}
          />
          {errors.title ? <p className="text-red-400 text-xs mt-1">{errors.title}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
            Description (Markdown supported)
          </label>
          <textarea
            className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all resize-none"
            onChange={(e) => {
              handleChange("description", e.target.value);
            }}
            placeholder="Description (Markdown supported)"
            rows={4}
            value={form.description}
          />
          {errors.description ? (
            <p className="text-red-400 text-xs mt-1">{errors.description}</p>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">Module</label>
          <ModuleSelect
            onChange={(value) => {
              handleChange("module", value);
            }}
            value={form.module}
          />
          {errors.module ? <p className="text-red-400 text-xs mt-1">{errors.module}</p> : null}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">Type</label>
            <select
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
              onChange={(e) => {
                handleChange("type", e.target.value as Roadmap_Item_Type);
              }}
              value={form.type}
            >
              <option value="feature">Feature</option>
              <option value="bug">Bug</option>
              <option value="insight">Insight</option>
              <option value="idea">Idea</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
              Status
            </label>
            <select
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
              onChange={(e) => {
                handleChange("status", e.target.value as Roadmap_Item_Status);
              }}
              value={form.status}
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Impact */}
          <div>
            <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
              Impact
            </label>
            <select
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
              onChange={(e) => {
                handleChange("impact_score", e.target.value);
              }}
              value={form.impact_score ?? ""}
            >
              <option value="">– Impact –</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
              Priority
            </label>
            <select
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
              onChange={(e) => {
                handleChange("priority", e.target.value);
              }}
              value={form.priority ?? ""}
            >
              <option value="">– Priority –</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Effort */}
          <div>
            <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
              Effort
            </label>
            <select
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
              onChange={(e) => {
                handleChange("effort_estimate", e.target.value);
              }}
              value={form.effort_estimate ?? ""}
            >
              <option value="">– Effort –</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-tracq-bright-blue">Progress</span>
            <span
              className={`text-lg font-medium ${form.status === "done" ? "text-green-500" : "text-tracq-bright-blue"}`}
            >
              {form.progress ?? 0}%
              {form.status === "done" && <span className="ml-1 text-xs">(Auto)</span>}
            </span>
          </div>
          <Slider
            color="blue"
            max={100}
            min={0}
            onChange={(value) => {
              handleChange("progress", value);
            }}
            step={1}
            value={form.progress ?? 0}
          />
          {form.status === "done" && (
            <p className="text-xs text-green-400 mt-2">
              ✓ Progress automatisch auf 100% gesetzt (kann manuell angepasst werden)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-tracq-bright-blue mb-2">
            Tags (comma-separated)
          </label>
          <input
            className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:border-transparent transition-all"
            onChange={(e) => {
              handleChange(
                "tags",
                e.target.value
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean),
              );
            }}
            placeholder="Tags (comma-separated)"
            type="text"
            value={form.tags?.join(", ") ?? ""}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <Button disabled={submitting} onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={submitting}
            loading={submitting}
            onClick={() => {
              handleSubmit().catch(() => {
                // NOTE: Error handling is done in handleSubmit
              });
            }}
            variant="primary"
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Named export only - no default export per project rules
